/**
 * Wpis historii pracy o pomiarze wpisanym ręcznie z platformy → klucz tłumaczenia.
 *
 * Nawodnienie i ciśnienie krwi wpisuje się z karty użytkownika (`patient.service`:
 * `addWaterIntake`, `deleteWaterIntake`, `addBloodPressure`), a backend zapisuje takie
 * wpisy pod kodami, których sekcja WORK-HISTORY nie zna — w tabeli stał wtedy surowy
 * `add_water_intake` albo `add_blood_pressure` (błąd 418). Ikonę te wiersze dostały
 * przy błędzie 419; brakowało samego opisu.
 *
 * Kody nadaje backend (poza tym repo), więc — tak jak przy strefach alarmowych —
 * rozpoznajemy rodzinę po fragmencie nazwy, a nie po dokładnym kodzie, i czytamy oba
 * pola wpisu: część z nich backend rozbija na czasownik w `action` i obszar
 * w `parameter` (`add` + `water_intake`).
 *
 * Etykiety wskazują tłumaczenia, KTÓRE JUŻ ISTNIEJĄ w komplecie plików i18n: to te same
 * teksty, którymi platforma potwierdza wpisanie pomiaru. Dzięki temu obie strony
 * aplikacji mówią o pomiarze tak samo i nie trzeba wymyślać zdań w dwudziestu językach,
 * z których nikt z nas nie zweryfikuje większości.
 *
 * Czyste funkcje, bez zależności od Angulara — testują się bez TestBed.
 */

/** Rodzaj pomiaru: fragmenty nazwy, którymi opisuje go backend, plus teksty opisu. */
interface Measurement {
  tokens: string[];
  /** Sama nazwa pomiaru — do wpisów, z których nie wynika czasownik. */
  labelKey: string;
  /** Czasownik → gotowe zdanie. Brak wpisu = zostaje sama nazwa pomiaru. */
  verbKeys: { [verb: string]: string };
}

const WATER_INTAKE: Measurement = {
  tokens: ['water', 'hydration'],
  labelKey: 'USER_DETAILS.WATER_INTAKE',
  verbKeys: { ADD: 'COMMON.ADD_ENTRY_WATER', DELETE: 'COMMON.DELETED_WATER_ENTRY' }
};

/**
 * Obok „pressure" łapiemy nazwy pojedynczych wartości, bo backend potrafi opisać wpis
 * nimi zamiast nazwą całego pomiaru. Zdania o usunięciu tu nie ma, bo platforma nie
 * umie usunąć pomiaru ciśnienia (`patient.service` zna tylko `addBloodPressure`) —
 * gdyby taki wpis przyszedł skądinąd, dostanie samą nazwę pomiaru zamiast zmyślonego
 * tłumaczenia.
 */
const BLOOD_PRESSURE: Measurement = {
  tokens: ['pressure', 'systolic', 'diastolic'],
  labelKey: 'USER_DETAILS.BLOOD_PRESSURE',
  verbKeys: { ADD: 'USER_DETAILS.BP_ADD_SUCCESS' }
};

/**
 * Pomiary wpisywane z mobilki (Sidly Health): EKG, waga i temperatura skóry. Backend
 * zapisuje je pod kodami `add_ecg`/`add_weight`/`add_temperature` (błąd 452, zrzut
 * z 2026-09-11) — bez wpisu tutaj w tabeli stał surowy kod akcji zamiast nazwy pomiaru,
 * dokładnie tak jak przy wodzie i ciśnieniu wcześniej. Żadna z trzech nie ma w plikach
 * i18n zdania „dodano pomiar" (mobilka nie ma po stronie web odpowiednika ekranu, z
 * którego dałoby się je pożyczyć), więc `verbKeys` zostają puste — `toMeasurementActionKey`
 * i tak spada na samą nazwę pomiaru, gdy czasownik nie ma wpisu.
 */
const ECG: Measurement = {
  tokens: ['ecg'],
  labelKey: 'USER_DETAILS.ECG',
  verbKeys: {}
};

const WEIGHT: Measurement = {
  tokens: ['weight'],
  labelKey: 'USER_DETAILS.WEIGHT',
  verbKeys: {}
};

const TEMPERATURE: Measurement = {
  tokens: ['temperature'],
  labelKey: 'SOS_REQUEST.TEMPERATURE',
  verbKeys: {}
};

const SAT_FILTER: Measurement = {
  tokens: ['sat_confirm'],
  labelKey: 'BACKEND.SAT_FILTER',
  verbKeys: {}
};

const MEASUREMENTS: Measurement[] = [WATER_INTAKE, BLOOD_PRESSURE, ECG, WEIGHT, TEMPERATURE, SAT_FILTER];

/** Kolejność bez znaczenia: wpis niesie tylko jeden czasownik. */
const VERBS: Array<{ verb: string, pattern: RegExp }> = [
  { verb: 'ADD', pattern: /add|create/ },
  { verb: 'DELETE', pattern: /delete|remove/ }
];

/** Pomiar nawodnienia — z kodu akcji ALBO z obszaru wpisu. */
export function isWaterIntakeAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(WATER_INTAKE, action, parameter);
}

/** Pomiar ciśnienia krwi — tak samo z obu pól wpisu. */
export function isBloodPressureAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(BLOOD_PRESSURE, action, parameter);
}

/** Pomiar EKG. */
export function isEcgAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(ECG, action, parameter);
}

/** Pomiar wagi. */
export function isWeightAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(WEIGHT, action, parameter);
}

/** Pomiar temperatury. */
export function isTemperatureAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(TEMPERATURE, action, parameter);
}

export function isSatFilterAction(action: string, parameter?: string): boolean {
  return isMeasurementOf(SAT_FILTER, action, parameter);
}

/**
 * `null` = wpis nie dotyczy pomiaru; wtedy wywołujący zostaje przy swoim
 * dotychczasowym zachowaniu.
 */
export function toMeasurementActionKey(action: string, parameter?: string): string {
  const measurement = MEASUREMENTS.filter(candidate => isMeasurementOf(candidate, action, parameter))[0];
  if (!measurement) {
    return null;
  }
  return measurement.verbKeys[toVerb(action) || toVerb(parameter)] || measurement.labelKey;
}

function isMeasurementOf(measurement: Measurement, action: string, parameter: string): boolean {
  const text = `${action || ''} ${parameter || ''}`.toLowerCase();
  return measurement.tokens.some(token => text.indexOf(token) !== -1);
}

function toVerb(value: string): string {
  const text = (value || '').toString().toLowerCase();
  const matched = VERBS.filter(candidate => candidate.pattern.test(text))[0];
  return matched ? matched.verb : null;
}
